import { Contract } from '@ethersproject/contracts'
import { JsonRpcProvider } from '@ethersproject/providers'
import { formatUnits } from '@ethersproject/units'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useActiveWeb3React } from '../../hooks'
import { useWalletModalToggle } from '../../state/application/hooks'

// Contract addresses (same across all chains via CREATE2)
const ROUTER_ADDRESS = '0xe4C5Cf259351d7877039CBaE0e7f92EB2Ab017EB'
const FACTORY_ADDRESS = '0x5f8017621825BC10D63d15C3e863f893946781F7'
const FARM_ADDRESS = '0x3dD417Ff4144bE35f203CB2CA569adF01Cd3574a'

// Networks where router/factory are deployed
const NETWORKS = [
  { chainId: 137, name: 'Polygon', rpcUrl: 'https://polygon-rpc.com' },
  { chainId: 56, name: 'BNB Chain', rpcUrl: 'https://bsc-dataseed.binance.org' },
  { chainId: 42161, name: 'Arbitrum', rpcUrl: 'https://arb1.arbitrum.io/rpc' },
  { chainId: 1285, name: 'Moonriver', rpcUrl: 'https://rpc.api.moonriver.moonbeam.network' }
]

// Networks where farm is deployed (Polygon and BSC only)
const FARM_NETWORKS = [
  { chainId: 137, name: 'Polygon', rpcUrl: 'https://polygon-rpc.com' },
  { chainId: 56, name: 'BNB Chain', rpcUrl: 'https://bsc-dataseed.binance.org' }
]

// Minimal ABIs
const ROUTER_ABI = [
  'function metaEnabled() view returns (bool)',
  'function metaSwitch()',
  'function owner() view returns (address)'
]

const FACTORY_ABI = [
  'function feeTo() view returns (address)',
  'function feeToSetter() view returns (address)',
  'function setFeeTo(address _feeTo)'
]

const FARM_ABI = [
  'function paused() view returns (bool)',
  'function owner() view returns (address)',
  'function totalSupply() view returns (uint256)',
  'function getAllRewardTokens() view returns (address[])',
  'function rewardData(address) view returns (address rewardsDistributor, uint256 rewardsDuration, uint256 periodFinish, uint256 rewardRate, uint256 lastUpdateTime, uint256 rewardPerTokenStored)',
  'function notifyRewardAmount(address _rewardsToken, uint256 reward)',
  'function recoverERC20(address tokenAddress, uint256 tokenAmount)'
]

const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function balanceOf(address) view returns (uint256)',
  'function decimals() view returns (uint8)'
]

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

const PageWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 20px;
  margin: 0 auto;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.text1};
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text3};
  margin-bottom: 24px;
`

const Card = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
`

const NetworkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) => theme.bg1};
  border-radius: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

const NetworkInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const NetworkName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
`

const ChainId = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text3};
`

const StatusBadge = styled.span<{ enabled: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ enabled }) => (enabled ? 'rgba(39, 174, 96, 0.1)' : 'rgba(255, 107, 107, 0.1)')};
  color: ${({ enabled }) => (enabled ? '#27ae60' : '#ff6b6b')};
`

const ActionButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled, theme }) => (disabled ? theme.bg3 : '#ff6b6b')};
  color: ${({ disabled }) => (disabled ? '#999' : 'white')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.9)};
  }
`

const ConnectButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  background: ${({ theme }) => theme.primary1};
  color: white;

  &:hover {
    opacity: 0.9;
  }
`

const InfoBox = styled.div`
  background: ${({ theme }) => theme.bg1};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  font-size: 13px;
  color: ${({ theme }) => theme.text2};
`

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.text3};
`

const SummaryCard = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 12px;
`

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};

  &:last-child {
    border-bottom: none;
  }
`

const SummaryCheck = styled.span<{ done: boolean }>`
  font-size: 16px;
  width: 24px;
  color: ${({ done }) => (done ? '#27ae60' : '#ffa500')};
`

const SummaryLabel = styled.span<{ done: boolean }>`
  font-size: 14px;
  color: ${({ done, theme }) => (done ? theme.text3 : theme.text1)};
  ${({ done }) => done && 'text-decoration: line-through;'}
`

const ProgressText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text3};
  margin-top: 12px;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
  margin: 32px 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChecklistIcon = styled.span<{ done: boolean }>`
  font-size: 16px;
  color: ${({ done }) => (done ? '#27ae60' : '#ffa500')};
`

const AddressText = styled.code`
  font-family: monospace;
  font-size: 11px;
  color: ${({ theme }) => theme.text3};
  word-break: break-all;
`

const SubText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text3};
  margin-top: 4px;
`

const RewardTokenCard = styled.div`
  background: ${({ theme }) => theme.bg1};
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
`

const RewardTokenHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const TokenSymbol = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
`

const TokenStatus = styled.span<{ active: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ active }) => (active ? 'rgba(39, 174, 96, 0.1)' : 'rgba(255, 165, 0, 0.1)')};
  color: ${({ active }) => (active ? '#27ae60' : '#ffa500')};
`

const RecoverInputWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 8px;
`

const RecoverInputContainer = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.bg2};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 6px;
  overflow: hidden;
`

const RecoverInput = styled.input`
  width: 120px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text1};
  font-size: 13px;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.text3};
  }
`

const MaxButton = styled.button`
  padding: 6px 10px;
  border: none;
  background: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text2};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.text1};
  }
`

const SmallButton = styled.button<{ disabled?: boolean; variant?: 'danger' | 'success' }>`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled, variant }) =>
    disabled ? '#666' : variant === 'success' ? '#27ae60' : '#ff6b6b'};
  color: white;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  margin-left: 4px;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.9)};
  }
`

interface NetworkStatus {
  chainId: number
  name: string
  metaEnabled: boolean | null
  owner: string | null
  loading: boolean
  switching: boolean
  error: string | null
}

interface FactoryStatus {
  chainId: number
  name: string
  feeTo: string | null
  feeToSetter: string | null
  loading: boolean
  executing: boolean
  error: string | null
}

interface RewardTokenData {
  address: string
  symbol: string
  decimals: number
  rewardsDistributor: string
  periodFinish: number
  rewardRate: string
  isActive: boolean
  contractBalance: string
  executing: boolean
}

interface FarmStatus {
  chainId: number
  name: string
  paused: boolean | null
  owner: string | null
  totalSupply: string | null
  rewardTokens: RewardTokenData[]
  loading: boolean
  error: string | null
}

export default function Admin() {
  const { account, chainId: connectedChainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const [networkStatuses, setNetworkStatuses] = useState<NetworkStatus[]>([])
  const [factoryStatuses, setFactoryStatuses] = useState<FactoryStatus[]>([])
  const [farmStatuses, setFarmStatuses] = useState<FarmStatus[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [recoverAmounts, setRecoverAmounts] = useState<{ [key: string]: string }>({})

  // Fetch status for all networks
  const fetchAllStatuses = useCallback(async () => {
    const statuses: NetworkStatus[] = await Promise.all(
      NETWORKS.map(async network => {
        try {
          const provider = new JsonRpcProvider(network.rpcUrl, network.chainId)
          const contract = new Contract(ROUTER_ADDRESS, ROUTER_ABI, provider)
          const [metaEnabled, owner] = await Promise.all([contract.metaEnabled(), contract.owner()])
          return {
            chainId: network.chainId,
            name: network.name,
            metaEnabled,
            owner,
            loading: false,
            switching: false,
            error: null
          }
        } catch (err) {
          console.error(`Failed to fetch status for ${network.name}:`, err)
          return {
            chainId: network.chainId,
            name: network.name,
            metaEnabled: null,
            owner: null,
            loading: false,
            switching: false,
            error: 'Failed to fetch'
          }
        }
      })
    )
    setNetworkStatuses(statuses)
    setInitialLoading(false)
  }, [])

  // Fetch factory status for all networks
  const fetchFactoryStatuses = useCallback(async () => {
    const statuses: FactoryStatus[] = await Promise.all(
      NETWORKS.map(async network => {
        try {
          const provider = new JsonRpcProvider(network.rpcUrl, network.chainId)
          const contract = new Contract(FACTORY_ADDRESS, FACTORY_ABI, provider)
          const [feeTo, feeToSetter] = await Promise.all([contract.feeTo(), contract.feeToSetter()])
          return {
            chainId: network.chainId,
            name: network.name,
            feeTo,
            feeToSetter,
            loading: false,
            executing: false,
            error: null
          }
        } catch (err) {
          console.error(`Failed to fetch factory status for ${network.name}:`, err)
          return {
            chainId: network.chainId,
            name: network.name,
            feeTo: null,
            feeToSetter: null,
            loading: false,
            executing: false,
            error: 'Failed to fetch'
          }
        }
      })
    )
    setFactoryStatuses(statuses)
  }, [])

  // Fetch farm status for Polygon and BSC
  const fetchFarmStatuses = useCallback(async () => {
    const statuses: FarmStatus[] = await Promise.all(
      FARM_NETWORKS.map(async network => {
        try {
          const provider = new JsonRpcProvider(network.rpcUrl, network.chainId)
          const farmContract = new Contract(FARM_ADDRESS, FARM_ABI, provider)

          const [paused, owner, totalSupply, rewardTokenAddresses] = await Promise.all([
            farmContract.paused(),
            farmContract.owner(),
            farmContract.totalSupply(),
            farmContract.getAllRewardTokens()
          ])

          // Fetch data for each reward token
          const rewardTokens: RewardTokenData[] = await Promise.all(
            rewardTokenAddresses.map(async (tokenAddr: string) => {
              const tokenContract = new Contract(tokenAddr, ERC20_ABI, provider)
              const [symbol, decimals, contractBalance, data] = await Promise.all([
                tokenContract.symbol(),
                tokenContract.decimals(),
                tokenContract.balanceOf(FARM_ADDRESS),
                farmContract.rewardData(tokenAddr)
              ])
              const periodFinish = data.periodFinish.toNumber()
              return {
                address: tokenAddr,
                symbol,
                decimals,
                rewardsDistributor: data.rewardsDistributor,
                periodFinish,
                rewardRate: formatUnits(data.rewardRate, decimals),
                isActive: periodFinish > Date.now() / 1000,
                contractBalance: formatUnits(contractBalance, decimals),
                executing: false
              }
            })
          )

          return {
            chainId: network.chainId,
            name: network.name,
            paused,
            owner,
            totalSupply: formatUnits(totalSupply, 18),
            rewardTokens,
            loading: false,
            error: null
          }
        } catch (err) {
          console.error(`Failed to fetch farm status for ${network.name}:`, err)
          return {
            chainId: network.chainId,
            name: network.name,
            paused: null,
            owner: null,
            totalSupply: null,
            rewardTokens: [],
            loading: false,
            error: 'Failed to fetch'
          }
        }
      })
    )
    setFarmStatuses(statuses)
  }, [])

  useEffect(() => {
    fetchAllStatuses()
    fetchFactoryStatuses()
    fetchFarmStatuses()
  }, [fetchAllStatuses, fetchFactoryStatuses, fetchFarmStatuses])

  // Check if connected account is router owner (same owner across all networks)
  const isRouterOwner = useMemo(() => {
    if (!account) return false
    const ownerAddress = networkStatuses.find(s => s.owner)?.owner
    return ownerAddress?.toLowerCase() === account.toLowerCase()
  }, [account, networkStatuses])

  // Check if connected account is factory feeToSetter
  const isFeeToSetter = useMemo(() => {
    if (!account) return false
    return factoryStatuses.some(s => s.feeToSetter?.toLowerCase() === account.toLowerCase())
  }, [account, factoryStatuses])

  // Check if connected account is farm owner
  const isFarmOwner = useMemo(() => {
    if (!account) return false
    return farmStatuses.some(s => s.owner?.toLowerCase() === account.toLowerCase())
  }, [account, farmStatuses])

  // Check if connected account is rewards distributor for any token
  const isRewardsDistributor = useCallback(
    (distributor: string) => {
      if (!account) return false
      return distributor.toLowerCase() === account.toLowerCase()
    },
    [account]
  )

  const routerOwnerAddress = networkStatuses.find(s => s.owner)?.owner
  const farmOwnerAddress = farmStatuses.find(s => s.owner)?.owner

  // Shorthand for legacy compatibility
  const isOwner = isRouterOwner

  // Network configurations for adding to wallet
  const NETWORK_CONFIGS: {
    [chainId: number]: {
      chainName: string
      rpcUrls: string[]
      nativeCurrency: { name: string; symbol: string; decimals: number }
      blockExplorerUrls: string[]
    }
  } = {
    137: {
      chainName: 'Polygon',
      rpcUrls: ['https://polygon-rpc.com'],
      nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
      blockExplorerUrls: ['https://polygonscan.com']
    },
    56: {
      chainName: 'BNB Chain',
      rpcUrls: ['https://bsc-dataseed.binance.org'],
      nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
      blockExplorerUrls: ['https://bscscan.com']
    },
    42161: {
      chainName: 'Arbitrum One',
      rpcUrls: ['https://arb1.arbitrum.io/rpc'],
      nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
      blockExplorerUrls: ['https://arbiscan.io']
    },
    1285: {
      chainName: 'Moonriver',
      rpcUrls: ['https://rpc.api.moonriver.moonbeam.network'],
      nativeCurrency: { name: 'MOVR', symbol: 'MOVR', decimals: 18 },
      blockExplorerUrls: ['https://moonriver.moonscan.io']
    }
  }

  // Switch to a specific network
  const switchNetwork = useCallback(
    async (targetChainId: number) => {
      const ethereum = (window as any).ethereum
      if (!ethereum) return

      const chainIdHex = `0x${targetChainId.toString(16)}`

      try {
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainIdHex }]
        })
      } catch (err) {
        // Error code 4902 means the chain hasn't been added to MetaMask
        if ((err as { code?: number }).code === 4902) {
          const config = NETWORK_CONFIGS[targetChainId]
          if (config) {
            try {
              await ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [
                  {
                    chainId: chainIdHex,
                    chainName: config.chainName,
                    rpcUrls: config.rpcUrls,
                    nativeCurrency: config.nativeCurrency,
                    blockExplorerUrls: config.blockExplorerUrls
                  }
                ]
              })
            } catch (addErr) {
              console.error('Failed to add network:', addErr)
            }
          }
        } else {
          console.error('Failed to switch network:', err)
        }
      }
    },
    [NETWORK_CONFIGS]
  )

  // Disable meta mode for a specific network
  const disableMeta = useCallback(
    async (targetChainId: number) => {
      if (!library || !account || connectedChainId !== targetChainId) {
        // Need to switch network first
        await switchNetwork(targetChainId)
        return
      }

      setNetworkStatuses(prev =>
        prev.map(s => (s.chainId === targetChainId ? { ...s, switching: true } : s))
      )

      try {
        const contract = new Contract(ROUTER_ADDRESS, ROUTER_ABI, library.getSigner(account))
        const tx = await contract.metaSwitch()
        await tx.wait()

        // Refresh status
        await fetchAllStatuses()
      } catch (err) {
        console.error('Failed to disable meta:', err)
        setNetworkStatuses(prev =>
          prev.map(s =>
            s.chainId === targetChainId
              ? { ...s, switching: false, error: 'Transaction failed' }
              : s
          )
        )
      }
    },
    [library, account, connectedChainId, switchNetwork, fetchAllStatuses]
  )

  // Disable protocol fees for a specific network
  const disableProtocolFees = useCallback(
    async (targetChainId: number) => {
      if (!library || !account || connectedChainId !== targetChainId) {
        await switchNetwork(targetChainId)
        return
      }

      setFactoryStatuses(prev =>
        prev.map(s => (s.chainId === targetChainId ? { ...s, executing: true } : s))
      )

      try {
        const contract = new Contract(FACTORY_ADDRESS, FACTORY_ABI, library.getSigner(account))
        const tx = await contract.setFeeTo(ZERO_ADDRESS)
        await tx.wait()
        await fetchFactoryStatuses()
      } catch (err) {
        console.error('Failed to disable protocol fees:', err)
        setFactoryStatuses(prev =>
          prev.map(s =>
            s.chainId === targetChainId
              ? { ...s, executing: false, error: 'Transaction failed' }
              : s
          )
        )
      }
    },
    [library, account, connectedChainId, switchNetwork, fetchFactoryStatuses]
  )

  // Stop rewards for a specific token on a specific network
  const stopRewards = useCallback(
    async (targetChainId: number, tokenAddress: string) => {
      if (!library || !account || connectedChainId !== targetChainId) {
        await switchNetwork(targetChainId)
        return
      }

      setFarmStatuses(prev =>
        prev.map(s =>
          s.chainId === targetChainId
            ? {
                ...s,
                rewardTokens: s.rewardTokens.map(t =>
                  t.address === tokenAddress ? { ...t, executing: true } : t
                )
              }
            : s
        )
      )

      try {
        const contract = new Contract(FARM_ADDRESS, FARM_ABI, library.getSigner(account))
        const tx = await contract.notifyRewardAmount(tokenAddress, 0)
        await tx.wait()
        await fetchFarmStatuses()
      } catch (err) {
        console.error('Failed to stop rewards:', err)
        setFarmStatuses(prev =>
          prev.map(s =>
            s.chainId === targetChainId
              ? {
                  ...s,
                  rewardTokens: s.rewardTokens.map(t =>
                    t.address === tokenAddress ? { ...t, executing: false } : t
                  )
                }
              : s
          )
        )
      }
    },
    [library, account, connectedChainId, switchNetwork, fetchFarmStatuses]
  )

  // Recover tokens from farm
  const recoverTokens = useCallback(
    async (targetChainId: number, tokenAddress: string, amount: string, decimals: number) => {
      if (!library || !account || connectedChainId !== targetChainId) {
        await switchNetwork(targetChainId)
        return
      }

      if (!amount || parseFloat(amount) <= 0) {
        console.error('Invalid amount')
        return
      }

      setFarmStatuses(prev =>
        prev.map(s =>
          s.chainId === targetChainId
            ? {
                ...s,
                rewardTokens: s.rewardTokens.map(t =>
                  t.address === tokenAddress ? { ...t, executing: true } : t
                )
              }
            : s
        )
      )

      try {
        const contract = new Contract(FARM_ADDRESS, FARM_ABI, library.getSigner(account))
        // Convert amount to wei
        const { parseUnits } = await import('@ethersproject/units')
        const amountWei = parseUnits(amount, decimals)
        const tx = await contract.recoverERC20(tokenAddress, amountWei)
        await tx.wait()
        // Clear the input
        setRecoverAmounts(prev => ({ ...prev, [`${targetChainId}-${tokenAddress}`]: '' }))
        await fetchFarmStatuses()
      } catch (err) {
        console.error('Failed to recover tokens:', err)
        setFarmStatuses(prev =>
          prev.map(s =>
            s.chainId === targetChainId
              ? {
                  ...s,
                  rewardTokens: s.rewardTokens.map(t =>
                    t.address === tokenAddress ? { ...t, executing: false } : t
                  )
                }
              : s
          )
        )
      }
    },
    [library, account, connectedChainId, switchNetwork, fetchFarmStatuses]
  )

  // Check completion status for checklist
  const routerComplete =
    networkStatuses.length > 0 && networkStatuses.every(s => s.metaEnabled === false)
  const factoryComplete =
    factoryStatuses.length > 0 && factoryStatuses.every(s => s.feeTo === ZERO_ADDRESS)
  // Farm rewards are stopped if all reward periods have ended (periodFinish in the past) or never started (periodFinish = 0)
  const farmRewardsComplete =
    farmStatuses.length > 0 &&
    farmStatuses.every(s => s.rewardTokens.length === 0 || s.rewardTokens.every(t => !t.isActive))
  // Farm recovery is complete when all leftover tokens have been recovered (balance = 0)
  const farmRecoveryComplete =
    farmStatuses.length > 0 &&
    farmStatuses.every(
      s =>
        s.rewardTokens.length === 0 ||
        s.rewardTokens.every(t => parseFloat(t.contractBalance) === 0)
    )

  return (
    <PageWrapper>
      <Title>Sunset Admin</Title>
      <Subtitle>Protocol shutdown checklist and controls</Subtitle>

      <InfoBox>
        {account ? (
          <div>
            <strong>Connected:</strong> <AddressText>{account}</AddressText>
          </div>
        ) : (
          <div>
            <strong>Wallet:</strong> Not connected (read-only)
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <strong>Admin Roles:</strong>
          <SubText>
            Router/Factory Owner: <AddressText>{routerOwnerAddress || 'Loading...'}</AddressText>
          </SubText>
          <SubText>
            Farm Owner: <AddressText>{farmOwnerAddress || 'Loading...'}</AddressText>
          </SubText>
        </div>
        {!account && (
          <ConnectButton onClick={toggleWalletModal} style={{ marginTop: 12 }}>
            Connect Wallet to Execute Actions
          </ConnectButton>
        )}
      </InfoBox>

      {/* SUMMARY CHECKLIST */}
      <SummaryCard>
        <SummaryTitle>Sunset Checklist</SummaryTitle>
        <SummaryRow>
          <SummaryCheck done={routerComplete}>{routerComplete ? '✓' : '○'}</SummaryCheck>
          <SummaryLabel done={routerComplete}>
            Disable relayer requirement on all routers
          </SummaryLabel>
        </SummaryRow>
        <SummaryRow>
          <SummaryCheck done={factoryComplete}>{factoryComplete ? '✓' : '○'}</SummaryCheck>
          <SummaryLabel done={factoryComplete}>Disable protocol fees on all factories</SummaryLabel>
        </SummaryRow>
        <SummaryRow>
          <SummaryCheck done={farmRewardsComplete}>{farmRewardsComplete ? '✓' : '○'}</SummaryCheck>
          <SummaryLabel done={farmRewardsComplete}>Stop all farm rewards</SummaryLabel>
        </SummaryRow>
        <SummaryRow>
          <SummaryCheck done={farmRecoveryComplete}>
            {farmRecoveryComplete ? '✓' : '○'}
          </SummaryCheck>
          <SummaryLabel done={farmRecoveryComplete}>Recover leftover reward tokens</SummaryLabel>
        </SummaryRow>
        <ProgressText>
          {
            [routerComplete, factoryComplete, farmRewardsComplete, farmRecoveryComplete].filter(
              Boolean
            ).length
          }
          /4 complete
        </ProgressText>
      </SummaryCard>

      {/* ROUTER CONTROLS */}
      <SectionTitle>
        <ChecklistIcon done={routerComplete}>{routerComplete ? '✓' : '○'}</ChecklistIcon>
        Router: Disable Relayer Requirement
      </SectionTitle>
      <Card>
        {initialLoading ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <>
            {networkStatuses.map(status => (
              <NetworkRow key={status.chainId}>
                <NetworkInfo>
                  <div>
                    <NetworkName>{status.name}</NetworkName>
                    <ChainId> ({status.chainId})</ChainId>
                  </div>
                </NetworkInfo>
                <NetworkInfo>
                  {status.error ? (
                    <StatusBadge enabled={false}>Error</StatusBadge>
                  ) : status.metaEnabled === null ? (
                    <StatusBadge enabled={false}>Unknown</StatusBadge>
                  ) : (
                    <StatusBadge enabled={!status.metaEnabled}>
                      {status.metaEnabled ? 'Meta ON' : 'Done'}
                    </StatusBadge>
                  )}
                  {isOwner && status.metaEnabled && (
                    <ActionButton
                      disabled={status.switching}
                      onClick={() => disableMeta(status.chainId)}
                    >
                      {status.switching
                        ? 'Switching...'
                        : connectedChainId === status.chainId
                        ? 'Disable'
                        : 'Switch & Disable'}
                    </ActionButton>
                  )}
                </NetworkInfo>
              </NetworkRow>
            ))}
          </>
        )}
      </Card>

      {/* FACTORY CONTROLS */}
      <SectionTitle>
        <ChecklistIcon done={factoryComplete}>{factoryComplete ? '✓' : '○'}</ChecklistIcon>
        Factory: Disable Protocol Fees
      </SectionTitle>
      <Card>
        {factoryStatuses.length === 0 ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <>
            {factoryStatuses.map(status => (
              <NetworkRow key={status.chainId}>
                <NetworkInfo>
                  <div>
                    <NetworkName>{status.name}</NetworkName>
                    <ChainId> ({status.chainId})</ChainId>
                    {status.feeTo && (
                      <SubText>
                        feeTo:{' '}
                        {status.feeTo === ZERO_ADDRESS ? (
                          'Disabled'
                        ) : (
                          <AddressText>{status.feeTo}</AddressText>
                        )}
                      </SubText>
                    )}
                  </div>
                </NetworkInfo>
                <NetworkInfo>
                  {status.error ? (
                    <StatusBadge enabled={false}>Error</StatusBadge>
                  ) : status.feeTo === ZERO_ADDRESS ? (
                    <StatusBadge enabled={true}>Done</StatusBadge>
                  ) : (
                    <StatusBadge enabled={false}>Fees Active</StatusBadge>
                  )}
                  {isFeeToSetter && status.feeTo !== ZERO_ADDRESS && (
                    <ActionButton
                      disabled={status.executing}
                      onClick={() => disableProtocolFees(status.chainId)}
                    >
                      {status.executing
                        ? 'Disabling...'
                        : connectedChainId === status.chainId
                        ? 'Disable Fees'
                        : 'Switch & Disable'}
                    </ActionButton>
                  )}
                </NetworkInfo>
              </NetworkRow>
            ))}
          </>
        )}
      </Card>

      {/* FARM CONTROLS */}
      <SectionTitle>
        <ChecklistIcon done={farmRewardsComplete && farmRecoveryComplete}>
          {farmRewardsComplete && farmRecoveryComplete ? '✓' : '○'}
        </ChecklistIcon>
        Farm: Stop Rewards & Recover Tokens
      </SectionTitle>
      {farmStatuses.length === 0 ? (
        <Card>
          <LoadingText>Loading...</LoadingText>
        </Card>
      ) : (
        farmStatuses.map(status => (
          <Card key={status.chainId}>
            <NetworkRow>
              <NetworkInfo>
                <div>
                  <NetworkName>{status.name}</NetworkName>
                  <ChainId> ({status.chainId})</ChainId>
                  <SubText>
                    Farm: <AddressText>{FARM_ADDRESS}</AddressText>
                  </SubText>
                  {status.totalSupply && (
                    <SubText>Total Staked: {parseFloat(status.totalSupply).toFixed(4)} LP</SubText>
                  )}
                </div>
              </NetworkInfo>
              <NetworkInfo>
                {status.error ? (
                  <StatusBadge enabled={false}>Error</StatusBadge>
                ) : status.paused ? (
                  <StatusBadge enabled={false}>Paused</StatusBadge>
                ) : (
                  <StatusBadge enabled={true}>Active</StatusBadge>
                )}
              </NetworkInfo>
            </NetworkRow>

            {status.rewardTokens.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <SubText style={{ marginBottom: 8 }}>
                  <strong>Reward Tokens:</strong>
                </SubText>
                {status.rewardTokens.map(token => (
                  <RewardTokenCard key={token.address}>
                    <RewardTokenHeader>
                      <div>
                        <TokenSymbol>{token.symbol}</TokenSymbol>
                        <TokenStatus active={token.isActive}>
                          {token.isActive
                            ? `Active until ${new Date(
                                token.periodFinish * 1000
                              ).toLocaleDateString()}`
                            : 'Ended'}
                        </TokenStatus>
                      </div>
                      <div>
                        {isRewardsDistributor(token.rewardsDistributor) && token.isActive && (
                          <SmallButton
                            disabled={token.executing}
                            onClick={() => stopRewards(status.chainId, token.address)}
                          >
                            {token.executing
                              ? 'Stopping...'
                              : connectedChainId === status.chainId
                              ? 'Stop Rewards'
                              : 'Switch & Stop'}
                          </SmallButton>
                        )}
                      </div>
                    </RewardTokenHeader>
                    <SubText>
                      Contract Balance: {parseFloat(token.contractBalance).toFixed(4)}{' '}
                      {token.symbol}
                    </SubText>
                    <SubText>
                      Distributor: <AddressText>{token.rewardsDistributor}</AddressText>
                    </SubText>
                    {isFarmOwner &&
                      !token.isActive &&
                      parseFloat(token.contractBalance || '0') > 0 && (
                        <RecoverInputWrapper>
                          <RecoverInputContainer>
                            <RecoverInput
                              type="text"
                              inputMode="decimal"
                              placeholder="Amount"
                              value={recoverAmounts[`${status.chainId}-${token.address}`] ?? ''}
                              onChange={e => {
                                const value = e.target?.value ?? ''
                                const key = `${status.chainId}-${token.address}`
                                setRecoverAmounts(prev => ({
                                  ...prev,
                                  [key]: value
                                }))
                              }}
                            />
                            <MaxButton
                              onClick={() =>
                                setRecoverAmounts(prev => ({
                                  ...prev,
                                  [`${status.chainId}-${token.address}`]: token.contractBalance
                                }))
                              }
                            >
                              MAX
                            </MaxButton>
                          </RecoverInputContainer>
                          <SmallButton
                            variant="success"
                            disabled={
                              token.executing ||
                              !recoverAmounts[`${status.chainId}-${token.address}`]
                            }
                            onClick={() =>
                              recoverTokens(
                                status.chainId,
                                token.address,
                                recoverAmounts[`${status.chainId}-${token.address}`] || '',
                                token.decimals
                              )
                            }
                          >
                            {token.executing
                              ? 'Recovering...'
                              : connectedChainId === status.chainId
                              ? 'Recover'
                              : 'Switch & Recover'}
                          </SmallButton>
                        </RecoverInputWrapper>
                      )}
                  </RewardTokenCard>
                ))}
              </div>
            )}
          </Card>
        ))
      )}
    </PageWrapper>
  )
}
